// ============================================
// Manor Miser - Local Game Manager (Offline Single Player)
// ============================================
// Thin wrapper that connects GameCore to SocketCallbacks for client-side play.

import { GameCore } from '../shared/game/GameCore';
import type { GameCoreCallbacks, TimerProvider } from '../shared/game/GameCore';
import type { SocketCallbacks } from '../network/SocketManager';
import type { Player, BetIncrement } from '../shared/types';
import { CHARACTER_TYPES } from '../shared/types';
import { BOT_NAMES, BOT_NAME_LIST } from '../shared/constants';

export class LocalGameManager {
  private gameCore: GameCore;
  private _humanId = 'local-player';
  private getCallbacks: () => SocketCallbacks;

  get playerId(): string {
    return this._humanId;
  }

  constructor(
    humanName: string,
    botCount: number,
    getCallbacks: () => SocketCallbacks
  ) {
    this.getCallbacks = getCallbacks;

    // Build players
    const players = new Map<string, Player>();
    const botIds: string[] = [];

    // Pick character type from BOT_NAMES mapping if name matches, otherwise random
    const humanAllowed = BOT_NAMES[humanName as keyof typeof BOT_NAMES];
    const humanCharType = humanAllowed
      ? humanAllowed[Math.floor(Math.random() * humanAllowed.length)]
      : CHARACTER_TYPES[Math.floor(Math.random() * CHARACTER_TYPES.length)];

    players.set(this._humanId, {
      id: this._humanId,
      name: humanName,
      isAlive: true,
      isConnected: true,
      isHost: true,
      isBot: false,
      characterType: humanCharType,
    });

    const humanNameLower = humanName.toLowerCase();
    const availableNames = this.shuffleArray(
      BOT_NAME_LIST.filter(n => n.toLowerCase() !== humanNameLower)
    );
    for (let i = 0; i < botCount; i++) {
      const botId = `bot-local-${i}`;
      const botName = availableNames[i % availableNames.length];
      const botAllowed = BOT_NAMES[botName as keyof typeof BOT_NAMES];
      players.set(botId, {
        id: botId,
        name: botName,
        isAlive: true,
        isConnected: true,
        isHost: false,
        isBot: true,
        characterType: botAllowed[Math.floor(Math.random() * botAllowed.length)],
      });
      botIds.push(botId);
    }

    const callbacks = this.createCallbacks();
    const timer: TimerProvider = {
      setTimeout: (cb, ms) => window.setTimeout(cb, ms),
      clearTimeout: (id) => window.clearTimeout(id),
    };

    this.gameCore = new GameCore(players, botIds, this._humanId, callbacks, timer);
  }

  startGame(): void {
    this.gameCore.startGame();
  }

  handleAddBet(amount: BetIncrement): void {
    this.gameCore.handleAddBet(this._humanId, amount);
  }

  handlePlayAgain(): void {
    this.gameCore.resetGame();
  }

  handleReturnToLobby(): void {
    this.gameCore.destroy();
  }

  destroy(): void {
    this.gameCore.destroy();
  }

  private createCallbacks(): GameCoreCallbacks {
    const humanId = this._humanId;
    const getCb = this.getCallbacks;

    return {
      onGameStartedForPlayer: (playerId, gameState, yourMoney) => {
        if (playerId === humanId) {
          getCb().onGameStarted?.(gameState, yourMoney);
        }
      },

      onRoundStartedForPlayer: (playerId, data) => {
        if (playerId === humanId) {
          getCb().onRoundStarted?.(
            data.roundNumber,
            data.phaseEndsAt,
            data.yourMoney,
            data.yourBet,
            data.alivePlayers
          );
        }
      },

      onBetProcessed: (bettingPlayerId, isBot, newBet, remainingMoney) => {
        // Always fire bet activity so the coin throw animation plays for everyone
        getCb().onBetActivity?.(bettingPlayerId);
        if (bettingPlayerId === humanId) {
          getCb().onBetUpdated?.(newBet, remainingMoney);
        }
      },

      onRoundReveal: (bets, phaseEndsAt) => {
        getCb().onRoundReveal?.(bets, phaseEndsAt);
      },

      onPlayerEliminated: (result) => {
        getCb().onPlayerEliminated?.(result);
      },

      onGameOver: (result) => {
        getCb().onGameOver?.(result);
      },
    };
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
}
